import React, {useEffect, useState} from 'react';
import {LessonContentRecordVideo} from '../../../../../data/models/LessonContent/LessonContentBase';
import {
  getMilisecondsFromTimeText,
  getTimeMilisecondsTextFromSeconds,
} from '../../../../../utils/getTimeTextFromSeconds';
import {lessonEditorText} from '../../../lessonEditorText';
import TextInput from '../../../../../uiToolkit/Inputs/TextInput';
import BoxVideoInput from '../../../../../uiToolkit/Inputs/BoxVideoInput';
import TimeInput from '../../../../../uiToolkit/Inputs/TimeInput/indes';
import Button from '../../../../../uiToolkit/Buttons/Button';
import '../../VideoSourceOptions/view/styles.css';
import EditorOptionsFooter from '../../../../../uiToolkit/Editor/EditorOptionsFooter';
import ReplaceNode from '../../ReplaceNode';
import {BaseContainerForwardProps} from '../../BaseOptionsContainer';

interface Props extends BaseContainerForwardProps {
  entityToEdit: LessonContentRecordVideo;
  onReRecord: () => void;
}

const RecordVideoOptionsUI = React.memo(
  ({
    entityToEdit,
    onEdit,
    isLoading,
    onDelete,
    isPublished,
    onSoftDelete,
    onReRecord,
  }: Props) => {
    const [duration, setDuration] = useState(0);
    const [timeStart, setTimeStart] = useState(
      getTimeMilisecondsTextFromSeconds(entityToEdit.startInSeconds),
    );
    const [timeEnd, setTimeEnd] = useState(
      getTimeMilisecondsTextFromSeconds(entityToEdit.endInSeconds),
    );
    const [title, setTitle] = useState(entityToEdit.title);
    const [currentContent, setCurrentContent] =
      useState<LessonContentRecordVideo>();

    useEffect(() => {
      setTimeStart(
        getTimeMilisecondsTextFromSeconds(entityToEdit.startInSeconds),
      );
      setTimeEnd(getTimeMilisecondsTextFromSeconds(entityToEdit.endInSeconds));
      setTitle(entityToEdit.title);
      setCurrentContent(undefined);
    }, [entityToEdit]);

    useEffect(() => {
      if (!currentContent && entityToEdit) {
        setCurrentContent(entityToEdit);
      }
    }, [entityToEdit, currentContent]);

    const onSave = () => {
      onEdit({
        ...entityToEdit,
        startInSeconds: getMilisecondsFromTimeText(timeStart),
        endInSeconds: getMilisecondsFromTimeText(timeEnd),
        durationInSeconds:
          getMilisecondsFromTimeText(timeEnd) -
          getMilisecondsFromTimeText(timeStart),
        title,
      } as LessonContentRecordVideo);
    };

    const startTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeStart);
        const endSeconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration && seconds < endSeconds;
      }
      return true;
    };

    const endTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration;
      }
      return true;
    };

    const changesWasMade = () => {
      return (
        entityToEdit.startInSeconds !== getMilisecondsFromTimeText(timeStart) ||
        entityToEdit.endInSeconds !== getMilisecondsFromTimeText(timeEnd) ||
        entityToEdit.title !== title.trim()
      );
    };

    const isValid = () => {
      return startTimeIsValid() && endTimeIsValid() && title.trim() !== '';
    };

    return (
      <div className="VideoSourceOptions">
        <TextInput
          heading={lessonEditorText.title}
          value={title}
          onChange={setTitle}
          placeholder={lessonEditorText.title}
          fieldError={title.trim() === ''}
          isMandatory
          uiType="box"
          className="Section"
          maxCharacters={50}
        />
        {currentContent?.contentUrl ? (
          <BoxVideoInput
            heading={lessonEditorText.source}
            source={currentContent.contentUrl}
            startTime={
              startTimeIsValid()
                ? getMilisecondsFromTimeText(timeStart)
                : undefined
            }
            endTime={
              endTimeIsValid() ? getMilisecondsFromTimeText(timeEnd) : undefined
            }
            setDuration={setDuration}
          />
        ) : null}
        <div className="TimePickersContainer">
          <TimeInput
            isValid={startTimeIsValid()}
            value={timeStart}
            onChange={setTimeStart}
            label={lessonEditorText.startTime}
            includeMiliseconds
          />
          <TimeInput
            isValid={endTimeIsValid()}
            value={timeEnd}
            onChange={setTimeEnd}
            label={lessonEditorText.endTime}
            includeMiliseconds
          />
        </div>
        <Button
          title={lessonEditorText.reRecordVideo}
          onClick={onReRecord}
          uiType="box"
          className="OptionsButton"
        />
        <ReplaceNode currentNode={entityToEdit} />
        <EditorOptionsFooter
          saveEnabled={changesWasMade() && isValid()}
          onSave={onSave}
          onDelete={onDelete}
          isLoading={isLoading}
          deleteDisabled={isPublished}
          onSoftDelete={onSoftDelete}
        />
      </div>
    );
  },
);

export default RecordVideoOptionsUI;
